import { useCallback, useState } from 'react';

export const MANUAL_NOTE_SECTIONS = [
  'anamnesis',
  'findings',
  'procedure',
  'therapy',
  'comment',
] as const;

export type ManualNoteSection = (typeof MANUAL_NOTE_SECTIONS)[number];

export type ManualNotes = Record<ManualNoteSection, string>;

const EMPTY_NOTES: ManualNotes = {
  anamnesis: '',
  findings: '',
  procedure: '',
  therapy: '',
  comment: '',
};

export const hasManualNotes = (notes: ManualNotes) =>
  MANUAL_NOTE_SECTIONS.some((section) => notes[section].replace(/<[^>]*>/g, '').trim() !== '');

export interface UseManualNotesResult {
  notes: ManualNotes;
  setSection: (section: ManualNoteSection, value: string) => void;
  replaceNotes: (next: Partial<ManualNotes>) => void;
}

export function useManualNotes(): UseManualNotesResult {
  const [notes, setNotes] = useState<ManualNotes>(EMPTY_NOTES);

  const setSection = (section: ManualNoteSection, value: string) => {
    setNotes((prev) => ({ ...prev, [section]: value }));
  };

  const replaceNotes = useCallback((next: Partial<ManualNotes>) => {
    const replaced = { ...EMPTY_NOTES };
    for (const section of MANUAL_NOTE_SECTIONS) replaced[section] = next[section] ?? '';
    setNotes(replaced);
  }, []);

  return { notes, setSection, replaceNotes };
}
